import React, { useState } from 'react';
import { Menu, X, Sparkles } from 'lucide-react';
import { getTranslations } from '../translations';

const Navigation = ({ currentPage, setCurrentPage, language, setLanguage }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const gold = '#C8A96E';
  const charcoal = '#1A1916';
  const secondary = '#6B6862';
  const tertiary = '#9A9690';
  const borderRest = '#E5E3DE';

  // translations
  const t = getTranslations('nav', language);

  const navItems = [
    { id: 'home',      label: t?.links?.home      || 'Home' },
    { id: 'about',     label: t?.links?.about     || 'About' },
    { id: 'infinity',  label: t?.links?.infinity  || 'Infinity' },
    { id: 'writings',  label: t?.links?.writings  || 'Writings' },
    { id: 'media',     label: t?.links?.media     || 'Media' },
    { id: 'contact',   label: t?.links?.contact   || 'Contact' }
  ];

  const handlePageChange = (pageId) => {
    setTimeout(() => {
      setCurrentPage(pageId);
      setIsMobileMenuOpen(false);
    }, 150);
  };

  return (
    <nav className="fixed top-0 inset-x-0 z-50 bg-white/95 backdrop-blur-md" style={{ borderBottom: `1px solid ${borderRest}` }}>
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/*— Row —*/}
        <div className="flex items-center justify-between py-3">
          {/* Logo */}
          <button
            onClick={() => handlePageChange('home')}
            className="flex items-center space-x-2.5 sm:space-x-3 group min-w-0"
          >
            <div className="flex-shrink-0">
              <img 
                src="Icon.png" 
                alt="Zeus Hernandez Logo"
                className="w-7 h-7 sm:w-8 sm:h-8 transition-opacity duration-300 group-hover:opacity-80"
              />
            </div>
            <span className="text-xs sm:text-sm font-medium uppercase transition-colors duration-300" style={{ color: charcoal, letterSpacing: '0.16em' }}>
              Zeus Hernandez
            </span>
          </button>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-6">
            {navItems.map((item) => {
              const active = currentPage === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handlePageChange(item.id)}
                  className="relative py-2 text-xs font-medium uppercase transition-colors duration-200"
                  style={{ color: active ? charcoal : secondary, letterSpacing: '0.12em' }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.color = charcoal; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.color = secondary; }}
                >
                  <span>{item.label}</span>
                  <span
                    className={`absolute left-1/2 -translate-x-1/2 -bottom-0.5 h-px transition-all duration-200 ${active ? 'w-6' : 'w-0'}`}
                    style={{ backgroundColor: gold }}
                  />
                </button>
              );
            })}
          </div>

          {/* Language Toggle - Desktop (Standalone) */}
          <div className="hidden lg:block">
            <div className="bg-white" style={{ border: `1px solid ${borderRest}` }}>
              <div className="flex items-center">
                <button
                  onClick={() => setLanguage('en')}
                  className="px-3 py-2 text-xs font-medium transition-colors duration-200"
                  style={{ color: language === 'en' ? '#fff' : secondary, backgroundColor: language === 'en' ? charcoal : 'transparent', letterSpacing: '0.1em' }}
                >
                  EN
                </button>
                <button
                  onClick={() => setLanguage('es')}
                  className="px-3 py-2 text-xs font-medium transition-colors duration-200"
                  style={{ color: language === 'es' ? '#fff' : secondary, backgroundColor: language === 'es' ? charcoal : 'transparent', letterSpacing: '0.1em' }}
                >
                  ES
                </button>
              </div>
            </div>
          </div>

          {/* Tablet nav */}
          <div className="hidden md:flex lg:hidden items-center space-x-1">
            {navItems.slice(0, 4).map((item) => {
              const active = currentPage === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handlePageChange(item.id)}
                  className="px-2 py-2 text-xs font-medium uppercase transition-colors"
                  style={{ color: active ? charcoal : secondary, letterSpacing: '0.1em' }}
                >
                  {item.label}
                </button>
              );
            })}
            
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="relative w-9 h-9 flex items-center justify-center transition-colors"
              style={{ border: `1px solid ${borderRest}`, color: charcoal }}
            >
              <Menu className="w-4 h-4" />
            </button>
          </div>

          {/* Language Toggle - Tablet (Standalone) */}
          <div className="hidden md:block lg:hidden">
            <div className="bg-white" style={{ border: `1px solid ${borderRest}` }}>
              <div className="flex items-center">
                <button
                  onClick={() => setLanguage('en')}
                  className="px-3 py-1.5 text-xs font-medium transition-colors duration-200"
                  style={{ color: language === 'en' ? '#fff' : secondary, backgroundColor: language === 'en' ? charcoal : 'transparent', letterSpacing: '0.1em' }}
                >
                  EN
                </button>
                <button
                  onClick={() => setLanguage('es')}
                  className="px-3 py-1.5 text-xs font-medium transition-colors duration-200"
                  style={{ color: language === 'es' ? '#fff' : secondary, backgroundColor: language === 'es' ? charcoal : 'transparent', letterSpacing: '0.1em' }}
                >
                  ES
                </button>
              </div>
            </div>
          </div>

          {/* Mobile: Menu Button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="relative w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center transition-colors duration-300 group"
              style={{ border: `1px solid ${borderRest}` }}
            >
              <div className="relative w-5 h-5">
                <Menu
                  className={`absolute inset-0 w-5 h-5 transition-all duration-300 ${
                    isMobileMenuOpen ? 'opacity-0 rotate-90' : 'opacity-100 rotate-0'
                  }`}
                  style={{ color: charcoal }}
                />
                <X
                  className={`absolute inset-0 w-5 h-5 transition-all duration-300 ${
                    isMobileMenuOpen ? 'opacity-100 rotate-0' : 'opacity-0 -rotate-90'
                  }`}
                  style={{ color: charcoal }}
                />
              </div>
            </button>
          </div>

          {/* Language Toggle - Mobile (Standalone) */}
          <div className="md:hidden">
            <div className="bg-white" style={{ border: `1px solid ${borderRest}` }}>
              <div className="flex items-center">
                <button
                  onClick={() => setLanguage('en')}
                  className="px-3 py-1.5 text-xs font-medium transition-colors duration-200"
                  style={{ color: language === 'en' ? '#fff' : secondary, backgroundColor: language === 'en' ? charcoal : 'transparent', letterSpacing: '0.1em' }}
                >
                  EN
                </button>
                <button
                  onClick={() => setLanguage('es')}
                  className="px-3 py-1.5 text-xs font-medium transition-colors duration-200"
                  style={{ color: language === 'es' ? '#fff' : secondary, backgroundColor: language === 'es' ? charcoal : 'transparent', letterSpacing: '0.1em' }}
                >
                  ES
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile/Tablet Menu */}
        <div
          className={`lg:hidden transition-all duration-300 ease-in-out ${
            isMobileMenuOpen ? 'max-h-96 opacity-100 mt-3 sm:mt-4' : 'max-h-0 opacity-0 mt-0'
          } overflow-hidden`}
        >
          <div className="bg-white p-3 sm:p-4 mx-1 sm:mx-0" style={{ border: `1px solid ${borderRest}` }}>
            <div className="space-y-1 sm:space-y-2 mobile-menu-scroll">
              {navItems.map((item, index) => {
                const active = currentPage === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => handlePageChange(item.id)}
                    className="w-full text-left px-3 sm:px-4 py-2.5 sm:py-3 font-medium transition-all duration-300 relative text-xs sm:text-sm uppercase"
                    style={{
                      color: active ? charcoal : secondary,
                      backgroundColor: active ? '#FAFAF8' : 'transparent',
                      letterSpacing: '0.12em',
                      animationDelay: `${index * 50}ms`,
                      animation: isMobileMenuOpen ? 'slideInUp 0.3s ease-out forwards' : 'none'
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <span>{item.label}</span>
                      {active && <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: gold }} />}
                    </div>
                    {active && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-px h-4 sm:h-6" style={{ backgroundColor: gold }} />
                    )}
                  </button>
                );
              })}
            </div>

            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4" style={{ borderTop: `1px solid ${borderRest}` }}>
              <div className="flex items-center justify-center gap-2 text-xs sm:text-sm" style={{ color: tertiary }}>
                <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" style={{ color: gold }} />
                <span className="text-center">{t?.tagline || 'Building the infinite future'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Backdrop for mobile menu */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/10 backdrop-blur-sm z-[-1]"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* NOTE: plain <style>, not styled-jsx */}
      <style>{`
        @keyframes slideInUp {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .mobile-menu-scroll::-webkit-scrollbar { width: 2px; }
        .mobile-menu-scroll::-webkit-scrollbar-track { background: transparent; }
        .mobile-menu-scroll::-webkit-scrollbar-thumb { background: #C8A96E; border-radius: 1px; }
      `}</style>
    </nav>
  );
};

export default Navigation;
